import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const normalizeProfessionType = (professionType) => {
  const value = (professionType || '').toUpperCase();

  if (value === 'INFORMATIKER' || value === 'INFORMATIK') return 'INFORMATIK';
  if (value === 'MEDIAMATIKER' || value === 'MEDIAMATIK') return 'MEDIAMATIK';
  if (value === 'KV') return 'KV';
  if (value === 'KUNDENDIALOG') return 'KUNDENDIALOG';
  if (value === 'DETAILHANDEL') return 'DETAILHANDEL';

  return 'ALL';
};

const getInitialFilterValue = (search, user, paramName, enableAutoDetection) => {
  const params = new URLSearchParams(search);
  const urlValue = params.get(paramName);

  if (urlValue) {
    return normalizeProfessionType(urlValue);
  }

  if (!enableAutoDetection) {
    return 'ALL';
  }

  if (user?.professionType) {
    return normalizeProfessionType(user.professionType);
  }

  return 'ALL';
};

/**
 * Hook to manage default profession filter logic based on user profile
 * @param {Object} user - Current logged in user object
 * @param {string} paramName - Name of the URL query parameter (default: 'professionType')
 * @param {boolean} enableAutoDetection - Whether to auto-detect profession from user profile (default: true)
 * @returns {Array} [professionFilter, setProfessionFilter]
 */
export const useDefaultProfessionFilter = (user, paramName = 'professionType', enableAutoDetection = true) => {
  const location = useLocation();
  const [filter, setFilter] = useState(() =>
    getInitialFilterValue(location.search, user, paramName, enableAutoDetection)
  );
  const [manualOverride, setManualOverride] = useState(false);

  // Wrapper for setting filter that marks it as manually overridden
  const setFilterWithOverride = (value) => {
    setFilter(value);
    setManualOverride(true);
  };

  useEffect(() => {
    // 1. Check URL params first (Always respect URL params as they might come from navigation)
    const params = new URLSearchParams(location.search);
    const urlValue = params.get(paramName);

    if (urlValue) {
      setFilter(normalizeProfessionType(urlValue));
      // We don't necessarily set manualOverride here, 
      // but URL navigation is usually explicit user intent.
      return; 
    }

    // If the user has manually selected a filter, do not override it with defaults
    if (manualOverride) {
      return;
    }

    if (!enableAutoDetection) {
      return;
    }

    // 2. Check User's profession
    // If we haven't set a filter manually yet, default to user's profession
    if (user?.professionType) {
      const normalizedProfession = normalizeProfessionType(user.professionType);
      if (normalizedProfession !== 'ALL') {
        setFilter(normalizedProfession);
      }
      // If the profession is unknown or 'ALL' we leave the filter as is (defaults
      // to ALL initially). We do NOT explicitly set 'ALL' here to avoid
      // overwriting if a transient state occurs. Multi-profession users default
      // to their primary profession and can switch via the filter.
    } 
    /* 
       To avoid loops, we include everything that is used inside.
    */

  }, [user?.professionType, location.search, paramName, enableAutoDetection]); // eslint-disable-line react-hooks/exhaustive-deps

  return [filter, setFilterWithOverride];
};
